import { useQueryClient } from '@tanstack/react-query';
import {
  ChatbotMessage,
  ChatbotSendMessageRequest,
  StreamEvent,
  StreamStartEvent,
  PlaceholderUpdateEvent,
  MessageUpdateEvent,
  MessageReferenceEvent,
  StreamEndEvent,
  StreamErrorEvent,
} from '../../models/chatbot/types';
import { sendMessageStream } from '../../services/chatbot';
import {
  chatMessagesQueryKey,
  transformSerializedCard,
} from '../../lib/chatbot-utils';

/**
 * Timeout duration (in milliseconds) for hung responses.
 * If no meaningful event is received within this time, the stream will be aborted.
 * Configurable via VITE_STREAMING_IDLE_TIMEOUT_MS environment variable.
 */
const STREAMING_IDLE_TIMEOUT_MS =
  Number(import.meta.env.VITE_STREAMING_IDLE_TIMEOUT_MS) || 30000; // 30 seconds default

/**
 * Helper function to update user message in React Query cache
 *
 * @param queryClient - React Query client instance
 * @param programId - Program ID for query key
 * @param threadId - Thread ID for query key
 * @param messageId - ID of the user message to update
 * @param updateFn - Function that receives old message and returns updated message
 */
const updateUserMessage = (
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  threadId: string,
  messageId: string,
  updateFn: (oldMessage: ChatbotMessage) => ChatbotMessage
) => {
  const queryKey = chatMessagesQueryKey(programId, threadId);
  queryClient.setQueryData(
    queryKey,
    (oldData: ChatbotMessage[] | undefined) => {
      if (!oldData) return oldData;

      return oldData.map((msg) =>
        msg.id === messageId && msg.role === 'user' ? updateFn(msg) : msg
      );
    }
  );
};

/**
 * Helper function to update assistant message in React Query cache
 *
 * @param queryClient - React Query client instance
 * @param programId - Program ID for query key
 * @param threadId - Thread ID for query key
 * @param messageId - ID of the assistant message to update
 * @param updateFn - Function that receives old message and returns updated message
 */
const updateAssistantMessage = (
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  threadId: string,
  messageId: string,
  updateFn: (oldMessage: ChatbotMessage) => ChatbotMessage
) => {
  const queryKey = chatMessagesQueryKey(programId, threadId);
  queryClient.setQueryData(
    queryKey,
    (oldData: ChatbotMessage[] | undefined) => {
      if (!oldData) return oldData;

      return oldData.map((msg) =>
        msg.id === messageId && msg.role === 'assistant' ? updateFn(msg) : msg
      );
    }
  );
};

/**
 * Handle stream_start events
 *
 * This event is fired when streaming begins and provides the initial message and thread IDs.
 *
 * Cache Actions:
 * - Captures threadId and assistantId for subsequent events
 * - Creates temporary user message with the sent content
 * - Creates temporary assistant message with empty content
 * - Adds both messages to the query cache for immediate UI display
 */
const handleStreamStart = (
  event: StreamStartEvent,
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  message: string,
  setStreamThreadId: (id: string) => void,
  setStreamAssistantId: (id: string) => void
) => {
  const threadId = event.data?.threadId;
  const assistantId = event.data?.assistantId;

  if (threadId) setStreamThreadId(threadId);
  if (assistantId) setStreamAssistantId(assistantId);

  // Add both user and assistant messages to cache
  if (event.data?.messageId && assistantId && threadId) {
    const queryKey = chatMessagesQueryKey(programId, threadId);

    const tempUserMessage: ChatbotMessage = {
      id: event.data.messageId,
      threadId: threadId,
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isStreaming: false,
    };

    const tempAssistantMessage: ChatbotMessage = {
      id: assistantId,
      threadId: threadId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isStreaming: true,
    };

    queryClient.setQueryData(
      queryKey,
      (oldData: ChatbotMessage[] | undefined) => {
        return oldData
          ? [...oldData, tempUserMessage, tempAssistantMessage]
          : [tempUserMessage, tempAssistantMessage];
      }
    );
  }
};

/**
 * Handle placeholder_update events
 *
 * This event is fired to update the placeholder text while the assistant is working.
 *
 * Cache Actions:
 * - Updates the placeholderText on the assistant message
 * - Triggers UI re-render to display updated placeholder
 */
const handlePlaceholderUpdate = (
  event: PlaceholderUpdateEvent,
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  streamThreadId: string | undefined,
  streamAssistantId: string | undefined
) => {
  if (!streamThreadId || !streamAssistantId) return;

  updateAssistantMessage(
    queryClient,
    programId,
    streamThreadId,
    streamAssistantId,
    (old) => ({
      ...old,
      placeholderText: event.data.text,
    })
  );
};

/**
 * Handle message_update events
 *
 * This event is fired continuously as the AI generates response text chunks.
 *
 * Cache Actions:
 * - Appends each text chunk to the assistant message content
 * - Maintains isStreaming: true flag to show typing indicator
 * - Updates query cache to trigger real-time text display
 * - Clears placeholder text once content starts arriving
 */
const handleMessageUpdate = (
  event: MessageUpdateEvent,
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  streamThreadId: string | undefined,
  streamAssistantId: string | undefined
) => {
  if (!streamThreadId || !streamAssistantId) return;

  updateAssistantMessage(
    queryClient,
    programId,
    streamThreadId,
    streamAssistantId,
    (old) => ({
      ...old,
      content: old.content + event.data.chunk,
      isStreaming: true,
      // Clear placeholder text once content starts arriving
      placeholderText: undefined,
    })
  );
};

/**
 * Handle message_reference events
 *
 * This event is fired when a reference is found in streaming content.
 *
 * Cache Actions:
 * - Adds a new reference to the assistant message references array
 * - Includes pre-built card data if available (no API call needed)
 * - Preserves existing references and other message data
 * - Updates query cache to trigger UI re-render with new reference
 */
const handleMessageReference = (
  event: MessageReferenceEvent,
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  streamThreadId: string | undefined,
  streamAssistantId: string | undefined
) => {
  if (!streamThreadId || !streamAssistantId) return;

  const card = transformSerializedCard(event.data.card);

  updateAssistantMessage(
    queryClient,
    programId,
    streamThreadId,
    streamAssistantId,
    (old) => ({
      ...old,
      references: [
        ...(old.references || []),
        {
          referenceType: event.data.type,
          referenceId: event.data.id,
          card,
        },
      ],
    })
  );
};

/**
 * Handle stream_end events
 *
 * This event is fired when streaming completes and provides final message data.
 *
 * Cache Actions:
 * - Sets isStreaming: false to remove typing indicator
 * - Clears placeholder text
 * - Invalidates thread list query to show new/updated thread in sidebar
 * - Falls back to full cache invalidation if message IDs are missing
 */
const handleStreamEnd = (
  event: StreamEndEvent,
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  threadId: string | undefined,
  streamThreadId: string | undefined,
  streamAssistantId: string | undefined
) => {
  const userMessage = event.data?.userMessage;
  const completedThreadId = userMessage?.threadId || streamThreadId || threadId;

  if (completedThreadId && streamAssistantId) {
    // Mark the streaming message as complete
    updateAssistantMessage(
      queryClient,
      programId,
      completedThreadId,
      streamAssistantId,
      (old) => ({
        ...old,
        isStreaming: false,
        placeholderText: undefined,
      })
    );

    // Only invalidate the thread list (for sidebar updates) - NOT the messages
    queryClient.invalidateQueries(['chat-thread-list', programId]);
  }
};

/**
 * Handle error events
 *
 * This function is called when streaming encounters an error or fails.
 *
 * Cache Actions:
 * - Only invalidates on complete failure where we need to reset state
 * - Preserves accumulated task data when possible
 * - Forces fresh data fetch only when absolutely necessary for recovery
 */
const handleError = (
  queryClient: ReturnType<typeof useQueryClient>,
  programId: string,
  threadId: string | undefined
) => {
  // Only invalidate if we have a specific thread that failed
  // This preserves task data in other threads
  if (threadId) {
    const queryKey = chatMessagesQueryKey(programId, threadId);
    queryClient.invalidateQueries(queryKey);
  }
};

/**
 * Hook for streaming chatbot messages with real-time placeholder updates
 *
 * This hook provides streaming functionality for chatbot messages. It handles
 * real-time updates to message content and placeholder text, automatically
 * updating the React Query cache as events are received.
 *
 * Event Flow:
 * 1. stream_start → Creates user + assistant messages
 * 2. placeholder_update → Updates placeholder text while waiting for content
 * 3. message_update → Appends text chunks, clears placeholder
 * 4. message_reference → Adds references to assistant message
 * 5. stream_end → Sets isStreaming: false, clears placeholder
 *
 * Cache Strategy:
 * - All updates go directly to React Query cache for immediate UI reactivity
 * - Automatic cache invalidation on errors for consistency
 * - Thread list updates when new conversations are created
 *
 * @param programId - The program ID for multi-tenant isolation
 * @returns Function to initiate streaming message send
 */
export const useChatbotStreaming = (programId: string) => {
  const queryClient = useQueryClient();

  return async (
    { threadId, message }: ChatbotSendMessageRequest,
    onEvent: (event: StreamEvent) => void,
    options?: { onTimeout?: (originalMessage: string) => void }
  ): Promise<void> => {
    let streamThreadId: string | undefined;
    let streamAssistantId: string | undefined;

    // Timeout management for hung responses
    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let streamCompleted = false;

    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (!streamCompleted) {
        timeoutId = setTimeout(() => {
          if (!streamCompleted) {
            abortController.abort();
            onEvent({ type: 'error', data: { error: 'timeout' } });
            options?.onTimeout?.(message);
          }
        }, STREAMING_IDLE_TIMEOUT_MS);
      }
    };

    const clearTimeoutTimer = () => {
      streamCompleted = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Start initial timeout
    resetTimeout();

    // Don't await - let streaming happen in background so React can re-render
    sendMessageStream({
      threadId,
      message,
      signal: abortController.signal,
      onEvent: (event) => {
        // Reset timeout on meaningful events (not ping)
        if (event.type !== 'ping') {
          resetTimeout();
        }
        // Pass event to the component first
        onEvent(event);

        // Handle different event types with type-safe switch statement
        switch (event.type) {
          case 'stream_start':
            handleStreamStart(
              event,
              queryClient,
              programId,
              message,
              (id) => {
                streamThreadId = id;
              },
              (id) => {
                streamAssistantId = id;
              }
            );
            break;

          case 'placeholder_update':
            // Add delay to avoid race condition with stream_start
            setTimeout(() => {
              handlePlaceholderUpdate(
                event,
                queryClient,
                programId,
                streamThreadId,
                streamAssistantId
              );
            }, 100);
            break;

          case 'message_update':
            handleMessageUpdate(
              event,
              queryClient,
              programId,
              streamThreadId,
              streamAssistantId
            );
            break;

          case 'message_reference':
            handleMessageReference(
              event,
              queryClient,
              programId,
              streamThreadId,
              streamAssistantId
            );
            break;

          case 'stream_end':
            clearTimeoutTimer();
            handleStreamEnd(
              event,
              queryClient,
              programId,
              threadId,
              streamThreadId,
              streamAssistantId
            );
            break;

          case 'error':
            clearTimeoutTimer();
            handleError(queryClient, programId, threadId);
            break;

          case 'ping':
            // This event doesn't need special handling in the cache
            break;
        }
      },
    }).catch((error) => {
      clearTimeoutTimer();

      // If this was an abort (timeout), it's already handled via onTimeout callback
      if (error.name === 'AbortError') {
        return;
      }

      // Handle promise rejection - be conservative about cache invalidation
      // Only invalidate if we have accumulated state that needs cleanup
      if (streamThreadId) {
        handleError(queryClient, programId, streamThreadId);
      }

      onEvent({
        type: 'error',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
    });
  };
};
